<script>
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import QRCode from './QRCode.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import TextArea from 'next/textarea/TextArea.vue';
import WhatsappReauthorize from '../channels/whatsapp/Reauthorize.vue';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';

export default {
  components: {
    SettingsSection,
    ImapSettings,
    SmtpSettings,
    QRCode,
    NextButton,
    DropdownMenu,
    TextArea,
    WhatsappReauthorize,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      hmacMandatory: false,
      turnstileEnabled: false,
      whatsAppInboxAPIKey: '',
      trackerEnabled: false,
      trackerType: 'amplitude',
      trackerSecret: '',
      showTrackerDropdown: false,
      isRequestingReauthorization: false,
      isSyncingTemplates: false,
      searchPageUrl: '',
      searchPageUrlDebounceTimer: null,
      allowedDomains: '',
      isUpdatingAllowedDomains: false,
    };
  },
  validations: {
    whatsAppInboxAPIKey: { required },
    trackerSecret: {
      required(value) {
        return (
          !this.trackerEnabled || this.trackerType === 'no_tracking' || !!value
        );
      },
    },
  },
  computed: {
    dialogueUrl() {
      return `${window.location.origin}/widget/dialogue?website_token=${this.inbox.website_token}`;
    },
    websiteDomainPlaceholder() {
      if (this.inbox.website_url) {
        try {
          const url = new URL(this.inbox.website_url);
          return `${url.origin}/search`;
        } catch {
          return 'https://your-website.com/search';
        }
      }
      return 'https://your-website.com/search';
    },
    trackerTypeOptions() {
      return [
        {
          action: 'select',
          value: 'amplitude',
          label: this.$t('AMPLITUDE'),
        },
        {
          action: 'select',
          value: 'facebook_pixel',
          label: this.$t('FACEBOOK_PIXEL'),
        },
        {
          action: 'select',
          value: 'google_analytics',
          label: this.$t('GOOGLE_TAG'),
        },
        {
          action: 'select',
          value: 'no_tracking',
          label: this.$t('NO_TRACKING'),
        },
      ];
    },
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    whatsappAppId() {
      return window.rookooConfig?.whatsappAppId;
    },
    isForwardingEnabled() {
      return !!this.inbox.forwarding_enabled;
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
    searchPageUrl() {
      this.debouncedUpdateSearchPageUrl();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.turnstileEnabled = this.inbox.turnstile_enabled || false;
      this.trackerEnabled = this.inbox.tracker_enabled || false;
      this.trackerType = this.inbox.tracker_type || 'amplitude';
      this.trackerSecret = this.inbox.tracker_secret || '';
      this.searchPageUrl = this.inbox.channel_options?.searchPageUrl || '';
      this.allowedDomains = this.inbox.allowed_domains || '';
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    handleTrackerEnabledChange() {
      // Only auto-save when disabling (to immediately persist the disabled state)
      // When enabling, let the user fill in the fields first and save manually
      if (!this.trackerEnabled) {
        this.updateTrackerConfig();
      }
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateTurnstileEnabled() {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: { turnstile_enabled: this.turnstileEnabled },
        });
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateAllowedDomains() {
      this.isUpdatingAllowedDomains = true;
      const sanitizedAllowedDomains = sanitizeAllowedDomains(
        this.allowedDomains
      );
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            allowed_domains: sanitizedAllowedDomains,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.allowedDomains = sanitizedAllowedDomains;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingAllowedDomains = false;
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateTrackerConfig() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            tracker_enabled: this.trackerEnabled,
            tracker_type: this.trackerType,
            tracker_secret: this.trackerSecret,
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    debouncedUpdateSearchPageUrl() {
      if (this.searchPageUrlDebounceTimer) {
        clearTimeout(this.searchPageUrlDebounceTimer);
      }

      this.searchPageUrlDebounceTimer = setTimeout(() => {
        this.updateSearchPageUrl();
      }, 1000); // 1 second debounce
    },
    async updateSearchPageUrl() {
      // Don't save if it's the same as the current value or during initialization
      if (
        this.searchPageUrl === (this.inbox.channel_options?.searchPageUrl || '')
      ) {
        return;
      }

      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            channel_options: {
              ...this.inbox.channel_options,
              searchPageUrl: this.searchPageUrl,
            },
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    handleTrackerTypeAction({ action, value }) {
      if (action === 'select') {
        this.trackerType = value;
        this.showTrackerDropdown = false;
        // Clear tracker secret when switching to no_tracking
        if (value === 'no_tracking') {
          this.trackerSecret = '';
        }
      }
    },
    toggleTrackerDropdown() {
      this.showTrackerDropdown = !this.showTrackerDropdown;
    },
    async handleReconfigure() {
      if (this.$refs.whatsappReauth) {
        await this.$refs.whatsappReauth.requestAuthorization();
      }
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
  },
};
</script>

<template>
  <div v-if="isATwilioChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
    <SettingsSection
      v-if="isATwilioWhatsAppChannel"
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
      :sub-title="
        $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
      "
    >
      <div class="flex justify-start items-center mt-2">
        <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
        </NextButton>
      </div>
    </SettingsSection>
  </div>
  <div v-else-if="isAVoiceChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_TITLE')"
      :sub-title="
        $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_SUBTITLE')
      "
    >
      <woot-code :script="inbox.voice_call_webhook_url" lang="html" />
    </SettingsSection>
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_TITLE')"
      :sub-title="
        $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_SUBTITLE')
      "
    >
      <woot-code :script="inbox.voice_status_webhook_url" lang="html" />
    </SettingsSection>
  </div>

  <div v-else-if="isALineChannel" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :sub-title="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsSection>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_HEADING')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.MESSENGER_SUB_HEAD')"
      >
        <woot-code
          :script="inbox.web_widget_script"
          lang="html"
          :codepen-title="`${inbox.name} - Chatwoot Widget Test`"
          enable-code-pen
        />
      </SettingsSection>

      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.QR_CODE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.QR_CODE_SUB_HEAD')"
      >
        <QRCode :url="dialogueUrl" />
        <div class="flex flex-col items-center justify-center px-5">
          <a target="_blank" rel="noopener noreferrer" :href="dialogueUrl">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.DIALOGUE_LINK') }}
          </a>
        </div>
      </SettingsSection>

      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.TITLE')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.SUBTITLE')"
      >
        <div class="flex flex-col w-full max-w-3xl gap-4">
          <TextArea
            v-model="allowedDomains"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.PLACEHOLDER')
            "
            auto-height
            min-height="8rem"
            class="w-full"
          />
          <div>
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isUpdatingAllowedDomains"
              @click="updateAllowedDomains"
            />
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      >
        <woot-code :script="inbox.hmac_token" />
        <template #subTitle>
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.chatwoot.com/docs/product/channels/live-chat/sdk/identity-validation/"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_LINK_TO_DOCS') }}
          </a>
        </template>
      </SettingsSection>
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
      >
        <div class="flex gap-2 items-center">
          <input
            id="hmacMandatory"
            v-model="hmacMandatory"
            type="checkbox"
            @change="handleHmacFlag"
          />
          <label for="hmacMandatory">
            {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
          </label>
        </div>
      </SettingsSection>

      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_BOT_PROTECTION')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_BOT_PROTECTION_SUB_TEXT')
        "
      >
        <div class="flex gap-2 items-center">
          <input
            id="turnstileEnabled"
            v-model="turnstileEnabled"
            type="checkbox"
            @change="updateTurnstileEnabled"
          />
          <label for="turnstileEnabled">
            {{ $t('INBOX_MGMT.EDIT.ENABLE_BOT_PROTECTION.LABEL') }}
          </label>
        </div>
      </SettingsSection>

      <SettingsSection
        :title="$t('ANALYTICS_CONFIGURATION')"
        :sub-title="$t('ANALYTICS_CONFIGURATION_DESCRIPTION')"
      >
        <div class="space-y-4">
          <div class="flex items-center gap-2">
            <input
              id="trackerEnabled"
              v-model="trackerEnabled"
              type="checkbox"
              @change="handleTrackerEnabledChange"
            />
            <label for="trackerEnabled">
              {{ $t('ENABLE_CUSTOM_ANALYTICS') }}
            </label>
          </div>

          <div
            v-if="!trackerEnabled"
            class="p-3 bg-gray-50 rounded-md text-sm text-gray-600"
          >
            {{ $t('ANALYTICS_FALLBACK_INFO') }}
          </div>

          <div v-if="trackerEnabled" class="space-y-4">
            <div>
              <label for="trackerType" class="block text-sm font-medium mb-1">
                {{ $t('TRACKER_TYPE') }}
              </label>
              <div class="relative">
                <button
                  class="w-full p-2 border border-gray-300 rounded-md text-left focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center"
                  @click="toggleTrackerDropdown"
                >
                  <span>{{
                    trackerType === 'amplitude'
                      ? $t('AMPLITUDE')
                      : trackerType === 'facebook_pixel'
                        ? $t('FACEBOOK_PIXEL')
                        : trackerType === 'google_analytics'
                          ? $t('GOOGLE_TAG')
                          : $t('NO_TRACKING')
                  }}</span>
                  <span class="i-lucide-chevron-down size-4" />
                </button>
                <DropdownMenu
                  v-if="showTrackerDropdown"
                  :menu-items="trackerTypeOptions"
                  @action="handleTrackerTypeAction"
                />
              </div>
            </div>

            <div v-if="trackerType !== 'no_tracking'">
              <label for="trackerSecret" class="block text-sm font-medium mb-1">
                {{ $t('API_KEY_PIXEL_ID') }}
              </label>
              <woot-input
                v-model="trackerSecret"
                type="password"
                :placeholder="$t('TRACKER_SECRET_PLACEHOLDER')"
              />
            </div>

            <div
              v-else
              class="p-3 bg-yellow-50 rounded-md text-sm text-yellow-800"
            >
              {{ $t('NO_TRACKING_INFO') }}
            </div>

            <NextButton
              :disabled="v$.trackerSecret.$invalid"
              @click="updateTrackerConfig"
            >
              {{ $t('SAVE_TRACKER_CONFIG') }}
            </NextButton>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        :title="$t('SEARCH_PAGE_CONFIGURATION')"
        :sub-title="$t('SEARCH_PAGE_CONFIGURATION_DESCRIPTION')"
      >
        <div>
          <label for="searchPageUrl" class="block text-sm font-medium mb-1">
            {{ $t('SEARCH_PAGE_URL') }}
          </label>
          <woot-input
            v-model="searchPageUrl"
            type="url"
            :placeholder="websiteDomainPlaceholder"
            data-testid="search-page-url-input"
          />
        </div>
      </SettingsSection>
    </div>
  </div>
  <div v-else-if="isAPIInbox" class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsSection>

    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <woot-code :script="inbox.hmac_token" />
    </SettingsSection>
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex gap-2 items-center">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsSection>
  </div>
  <div v-else-if="isAClassicEmailChannel">
    <div class="mx-8">
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :sub-title="
          isForwardingEnabled
            ? $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')
            : ''
        "
      >
        <woot-code
          v-if="isForwardingEnabled"
          :script="inbox.forward_to_email"
        />
        <div
          v-else
          class="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
        >
          <p class="text-sm text-yellow-800 dark:text-yellow-200 mb-0">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_NOT_CONFIGURED') }}
          </p>
        </div>
      </SettingsSection>
    </div>
    <ImapSettings :inbox="inbox" />
    <SmtpSettings v-if="inbox.imap_enabled" :inbox="inbox" />
  </div>
  <div v-else-if="isAWhatsAppChannel && !isATwilioChannel">
    <div v-if="inbox.provider_config" class="mx-8">
      <!-- Embedded Signup Section -->
      <template v-if="isEmbeddedSignupWhatsApp">
        <SettingsSection
          v-if="whatsappAppId"
          :title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_TITLE')
          "
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_SUBHEADER')
          "
        >
          <div class="flex gap-4 items-center">
            <p class="text-sm text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_DESCRIPTION'
                )
              }}
            </p>
            <NextButton @click="handleReconfigure">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_BUTTON') }}
            </NextButton>
          </div>
        </SettingsSection>
      </template>

      <!-- Manual Setup Section -->
      <template v-else>
        <SettingsSection
          :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.webhook_verify_token" />
        </SettingsSection>
        <SettingsSection
          :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')
          "
        >
          <woot-code :script="inbox.provider_config.api_key" />
        </SettingsSection>
        <SettingsSection
          :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')"
          :sub-title="
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER')
          "
        >
          <div
            class="flex flex-1 justify-between items-center mt-2 whatsapp-settings--content"
          >
            <woot-input
              v-model="whatsAppInboxAPIKey"
              type="text"
              class="flex-1 mr-2 [&>input]:!mb-0"
              :placeholder="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
                )
              "
            />
            <NextButton
              :disabled="v$.whatsAppInboxAPIKey.$invalid"
              @click="updateWhatsAppInboxAPIKey"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON')
              }}
            </NextButton>
          </div>
        </SettingsSection>
      </template>
      <SettingsSection
        :title="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
        :sub-title="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
        "
      >
        <div class="flex justify-start items-center mt-2">
          <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
          </NextButton>
        </div>
      </SettingsSection>
    </div>
    <WhatsappReauthorize
      v-if="isEmbeddedSignupWhatsApp"
      ref="whatsappReauth"
      :inbox="inbox"
      class="hidden"
    />
  </div>
</template>

<style lang="scss" scoped>
.whatsapp-settings--content {
  ::v-deep input {
    margin-bottom: 0;
  }
}
</style>
